import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { ref } from 'lit/directives/ref.js';
import { styleMap } from 'lit/directives/style-map.js';
import { Analytics, ANALYTICS_EVENTS } from '../utils/analytics.js';
import { preload as preloadFingerprint } from '../utils/fingerprintManager.js';
import { BotDetector } from '../utils/botDetector.js';
// Import Amplitude module explicitly so it only bundles with this widget
import * as amplitudeModule from '../utils/amplitudeAnalytics.js';
// Import partner theme detection and injection
import { applyPartnerTheme } from './partnerThemes.js';
import { FEATURE_FLAG_DEFAULTS, FEATURE_FLAG_REFRESH_INTERVAL_MS } from '../../config/featureFlags.js';

// ── Debug logger ──────────────────────────────────────────────────────────────
// __DEBUG_BUILD__ is replaced at build time:
//   • true  → staging / devint builds  (logs on by default)
//   • false → production build         (logs off by default)
//
// Override at runtime from any browser console without a rebuild:
//   Enable:  localStorage.setItem('gist_debug', 'true')  then reload
//   Disable: localStorage.removeItem('gist_debug')       then reload
const _debugEnabled = __DEBUG_BUILD__ || localStorage.getItem('gist_debug') === 'true';
const logger = {
  log:   (...a) => _debugEnabled && console.log('[GistWidget]',   ...a),
  warn:  (...a) => _debugEnabled && console.warn('[GistWidget]',  ...a),
  error: (...a) =>                  console.error('[GistWidget]', ...a), // always on
};

// Analytics events specific to questions bar widget
const QUESTIONS_BAR_ANALYTICS_EVENTS = {
  QUESTIONS_BAR_WIDGET_INITIALIZED: 'Questions Bar Widget Initialized',
  QUESTIONS_BAR_WIDGET_VISIBLE: 'Questions Bar Widget Visible',
  QUESTION_CLICKED: 'Questions Bar Widget Question Clicked',
  QUESTION_VISIBLE: 'Questions Bar Widget Question Visible',
  QUESTIONS_REFRESHED: 'Questions Bar Widget Questions Refreshed',
  SETTINGS_CHANGED: 'Questions Bar Widget Settings Changed',
  PAGE_CLOSE: 'Questions Bar Widget Page Close',
  API_CALL: 'Questions Bar Widget API Call',
  API_ERROR: 'Questions Bar Widget API Error',
  QUESTION_TOOLTIP_HOVERED: 'Questions Bar Widget Question Tooltip Hovered',
};

// Keep track of analytics initialization state
let isAnalyticsInitialized = false;

// URL patterns that indicate pages where widget should not be initialized
const EXCLUDED_URL_PATTERNS = [
  /\/login/i,
  /\/signin/i,
  /\/sign-in/i,
  /\/auth/i,
  /\/account/i,
  /\/bank/i,
  /\/payment/i,
  /\/checkout/i,
  /\/billing/i,
  /\/wallet/i,
  /\/password/i,
  /\/reset-password/i,
  /\/forgot-password/i,
  /\/otp/i,
  /\/2fa/i,
  /\/mfa/i,
  /\/sso/i,
  /\/oauth/i,
  /\/admin/i,
  /\/dashboard\/settings/i,
  /\/profile\/settings/i,
  /\/settings\/account/i,
  /secure\./i,
  /\.bank\./i,
  /\.paypal\./i,
  /\/transfer/i,
  /\/withdraw/i,
  /\/deposit/i,
];

// Title patterns that indicate sensitive pages
const EXCLUDED_TITLE_PATTERNS = [
  /sign\s*in/i,
  /log\s*in/i,
  /login/i,
  /authentication/i,
  /password/i,
  /reset\s*password/i,
  /forgot\s*password/i,
  /verify\s*identity/i,
  /two[\s-]*factor/i,
  /2fa/i,
  /otp/i,
  /security\s*code/i,
  /payment/i,
  /checkout/i,
  /billing/i,
  /credit\s*card/i,
  /bank\s*account/i,
  /account\s*settings/i,
  /profile\s*settings/i,
  /admin\s*panel/i,
  /dashboard\s*settings/i,
];

// Selectors for sensitive form elements that indicate sensitive pages
const SENSITIVE_FORM_SELECTORS = [
  'input[type="password"]',
  'input[name*="password" i]',
  'input[name*="ssn" i]',
  'input[name*="social" i]',
  'input[name*="card" i]',
  'input[name*="cvv" i]',
  'input[name*="cvc" i]',
  'input[name*="iban" i]',
  'input[name*="account" i][type*="number" i]',
  'input[autocomplete="cc-number"]',
  'input[autocomplete="current-password"]',
  'input[autocomplete="new-password"]',
  'input[placeholder*="password" i]',
  'input[placeholder*="cvv" i]',
  'input[placeholder*="cvc" i]',
];

/**
 * Checks if the current page should be excluded from widget initialization
 * based on URL patterns, title patterns, and sensitive form presence.
 */
function shouldSkipWidgetInitialization() {
  const url = window.location.href.toLowerCase();
  const title = document.title.toLowerCase();

  // Check URL patterns
  for (const pattern of EXCLUDED_URL_PATTERNS) {
    if (pattern.test(url)) {
      return true;
    }
  }

  // Check title patterns
  for (const pattern of EXCLUDED_TITLE_PATTERNS) {
    if (pattern.test(title)) {
      return true;
    }
  }

  // Check for sensitive form elements
  for (const selector of SENSITIVE_FORM_SELECTORS) {
    if (document.querySelector(selector)) {
      return true;
    }
  }

  return false;
}

/**
 * Extracts page content for questions API
 */
function extractPageContent() {
  const contentSelectors = [
    "main",
    "article",
    '[role="main"]',
    ".content",
    "#content",
    ".post",
    ".article",
    "[data-content]",
    ".entry-content",
    ".post-content"
  ];

  const removeSelectors = [
    "script",
    "style",
    "noscript",
    "template",
    "svg",
    "canvas",
    "iframe",
    "nav",
    "footer",
    "header",
    "aside",
    "form",
    "button"
  ];

  let contentElements = [];

  // Find all main content containers
  for (const selector of contentSelectors) {
    const elements = document.querySelectorAll(selector);
    elements.forEach(el => {
      if (el && el.textContent?.trim()) {
        contentElements.push(el);
      }
    });
  }

  // Use all found content elements, or fallback to body
  let root = null;
  if (contentElements.length > 0) {
    if (contentElements.length === 1) {
      root = contentElements[0];
    } else {
      const fragment = document.createDocumentFragment();
      contentElements.forEach(el => {
        fragment.appendChild(el.cloneNode(true));
      });
      const container = document.createElement('div');
      container.appendChild(fragment);
      root = container;
    }
  } else {
    root = document.body;
  }

  // Clone so we don't modify the real DOM
  const clone = root.cloneNode(true);

  // Remove unwanted nodes
  clone.querySelectorAll(removeSelectors.join(",")).forEach((el) => el.remove());

  let content = clone.textContent || "";

  content = content
    .replace(/\s+/g, " ")
    .trim()
    .substring(0, 20000);

  return content;
}

const STAGING_API_KEYS = [
  '726af850356941f9ae10987fe0f0328b',
  '4f32b284a9f24baeb462f86c495b8e64',
  'df693593119d49aa978ee0de1f282c4b',
  '41ec28f08f0843eb82429b97c166070c',
  'efb4e8229a114013837e3dfd14e69287',
  '16221bdbac9140f2b49df7b272c7b648',
  '428fc0227ccc4ca4aa05b4b8ca3b4a81',
  'cfea9d9acd9c45b1a4a2fc0380b7ceeb',
];

function isStagingKey(apiKey) {
  return STAGING_API_KEYS.includes(apiKey);
}

/**
 * Parses the text fragment value from a URL's hash.
 * e.g. 'https://example.com/page#:~:text=hello,world' → 'hello,world'
 *      'https://example.com/page#section:~:text=foo'  → 'foo'
 * Returns null if no text fragment is present.
 */
function parseTextFragment(url) {
  try {
    const hash = new URL(url).hash; // includes leading '#'
    const marker = ':~:text=';
    const idx = hash.indexOf(marker);
    if (idx !== -1) {
      return hash.slice(idx + marker.length); // everything after ':~:text='
    }
  } catch {
    // invalid URL — fall through
  }
  return null;
}

/**
 * Builds the final href from a URL and an optional text-fragment value.
 * Shared by openLink() and the async redirector path so both produce
 * identical URLs without duplicating logic.
 *
 * - Query parameters are preserved via parsed.search ('' or '?k=v&…').
 * - The text-fragment value is appended as-is (already encoded by the
 *   redirector) to avoid double-encoding.
 * - Any named anchor before ':~:text=' in the original hash is kept.
 */
function buildHref(url, textFragment = null) {
  try {
    const parsed = new URL(url);
    if (textFragment) {
      const existingHash = parsed.hash.replace(/^#/, '');
      const anchorPart   = existingHash.split(':~:text=')[0];
      const fragment     = (anchorPart ? anchorPart + ':~:text=' : ':~:text=') + textFragment;
      return parsed.origin + parsed.pathname + parsed.search + '#' + fragment;
    }
    return parsed.href;
  } catch {
    return url;
  }
}

/**
 * Opens a URL in a new tab via a programmatic anchor click.
 *
 * Safari (iOS) / mobile WebKit quirks handled:
 *   1. The <a> element MUST be in the DOM before .click() — detached-element
 *      clicks are silently ignored by mobile WebKit.
 *   2. The element is hidden off-screen (position:fixed, far off-canvas) so it
 *      never causes a visual flash or layout shift.
 *   3. It is removed on the next tick so the DOM stays clean.
 *
 * Query-param / text-fragment handling is delegated to buildHref().
 */
function openLink(url, textFragment = null) {
  const href = buildHref(url, textFragment);
  logger.log('openLink called — href:', href,
    '| ua:', navigator.userAgent.slice(0, 80));

  const link       = document.createElement('a');
  link.href        = href;
  link.target      = '_blank';
  link.rel         = 'noopener noreferrer';
  // Keep the element invisible but in-document so mobile Safari honours the click.
  link.style.cssText = 'position:fixed;top:-9999px;left:-9999px;opacity:0;pointer-events:none;';

  const bodyAvailable = !!document.body;
  logger.log('openLink — document.body available:', bodyAvailable,
    '| link.href after set:', link.href);

  if (!bodyAvailable) {
    logger.error('openLink FAILED — document.body is not available');
    return;
  }

  document.body.appendChild(link);
  logger.log('openLink — link appended to body, calling link.click()');
  link.click();
  logger.log('openLink — link.click() returned');
  // Remove on the next tick — the click event must complete its dispatch first.
  setTimeout(() => link.remove(), 0);
}

/**
 * Fetch questions from the API
 */
async function fetchQuestions(apiBaseUrl, apiKey, content, title, url, questionType = 'all', pageRequestId = null) {
  try {
    const pageTitle = title || document.title || '';
    const pageUrl = url || window.location.href || '';

    const baseUrl = isStagingKey(apiKey)
        ? 'https://api-staging.prorata.ai/services/v1/question-bar-svc'
        : (apiBaseUrl || __QUESTIONS_API_BASE_URL__);
    const key = apiKey;

    const endpoint = questionType === 'sponsored'
      ? `${baseUrl}/question_generator/sponsored`
      : `${baseUrl}/question_generator/content`;

    // Track individual API call with question type and page request correlation
    Analytics.track(QUESTIONS_BAR_ANALYTICS_EVENTS.API_CALL, {
      question_type: questionType,
      page_request_id: pageRequestId,
      endpoint: endpoint,
      api_base_url: baseUrl,
      content_length: content?.length || 0,
      page_url: pageUrl,
      page_title: pageTitle,
      has_api_key: !!key,
    });

    // Retry configuration
    const MAX_RETRIES = 3;
    const BASE_DELAY_MS = 500;
    let lastError = null;
    let response = null;

    for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
      try {
        response = await fetch(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': '*/*',
            'X-API-Key': key
          },
          body: JSON.stringify({
            url: pageUrl,
            title: pageTitle,
            content: content,
            type: questionType,
          }),
        });

        // Success or non-retryable error (4xx client errors)
        if (response.ok || (response.status >= 400 && response.status < 500)) {
          break;
        }

        // Retryable error (5xx server errors) - throw to trigger retry
        throw new Error(`API error: ${response.status}`);
      } catch (fetchError) {
        lastError = fetchError;

        // Don't retry on the last attempt - outer catch block will log final error
        if (attempt >= MAX_RETRIES) {
          break;
        }

        // Track intermediate retry attempt as API error (final error logged by outer catch)
        Analytics.track(QUESTIONS_BAR_ANALYTICS_EVENTS.API_ERROR, {
          error_message: fetchError instanceof Error ? fetchError.message : 'Unknown error',
          api_endpoint: endpoint,
          question_type: questionType,
          page_url: pageUrl || window.location.href || '',
          page_title: pageTitle || '',
          retry_attempt: attempt,
        });

        // Exponential backoff: 500ms, 1000ms, 2000ms
        const delay = BASE_DELAY_MS * Math.pow(2, attempt - 1);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }

    if (!response || !response.ok) {
      throw lastError || new Error('Failed to fetch questions after retries');
    }

    const data = await response.json();
    const questions = data.questions || [];

    if (questionType === 'sponsored') {
      return questions.map(q => ({
        id: q.question_id || `sponsored-${Math.random()}`,
        text: q.question,
        type: 'sponsored',
        url: q.landing_page_url,
        isDefaultUrl: q.is_default_url
      }));
    } else {
      return questions.map(q => ({
        id: q.question_id || `content-${Math.random()}`,
        text: q.question,
        type: 'related',
        teaser: q.metadata?.teaser || null,
        context: q.metadata?.context || null,
        isDefaultUrl: q.is_default_url
      }));
    }
  } catch (error) {
    logger.error('Failed to fetch questions:', error);

    // Track API error
    try {
      const baseUrl = apiBaseUrl || __QUESTIONS_API_BASE_URL__;
      const endpointPath = questionType === 'sponsored'
        ? '/question_generator/sponsored'
        : '/question_generator/content';
      const endpoint = `${baseUrl}${endpointPath}`;

      Analytics.track(QUESTIONS_BAR_ANALYTICS_EVENTS.API_ERROR, {
        error_message: error instanceof Error ? error.message : 'Unknown error',
        api_endpoint: endpoint,
        question_type: questionType,
        page_url: pageUrl || window.location.href || '',
        page_title: title || document.title || '',
      });
    } catch (trackingError) {
      logger.error('Failed to track API error:', trackingError);
    }

    return [];
  } 
}

@customElement('gist-questions-bar-widget')
export class QuestionsBarWidget extends LitElement {
  // Input properties
  @property({ type: String, attribute: 'api-key' })
  accessor apiKey = '';

  @property({ type: String, attribute: 'user-id' })
  accessor userId = '';

  // Force display-mode to always be 'mixed', regardless of the value partners
  // pass via the `display-mode` attribute. The custom converter ignores the
  // incoming attribute value so partner overrides (e.g. "split") are dropped.
  @property({
    type: String,
    attribute: 'display-mode',
    converter: {
      fromAttribute: () => 'mixed',
      toAttribute: () => 'mixed'
    }
  })
  accessor displayMode = 'mixed'; // forced to 'mixed'

  @property({ type: String, attribute: 'mix-pattern' })
  accessor mixPattern = 'alternate'; // 'random' | 'alternate'

  @property({ type: String, attribute: 'answer-provider' })
  accessor answerProvider = 'mypages'; // needs to be mypages so that this does not fail on server end
  // 'brave' | 'gist.ai' | 'perplexity' | 'mypages'

  @property({ type: Boolean, attribute: 'disabled' })
  accessor disabled = false;

  @property({ type: String, attribute: 'page-url' })
  accessor pageUrl = '';

  // Styling properties
  @property({ type: String, attribute: 'bar-background' })
  accessor barBackground = '#ffffff';

  @property({ type: String, attribute: 'top-offset' })
  accessor topOffset = '0';

  @property({ type: String, attribute: 'z-index' })
  accessor zIndexValue = '9999';

  @property({ type: Boolean, attribute: 'pinned' })
  accessor pinned = false;

  @property({ type: String, attribute: 'pill-background-split' })
  accessor pillBackgroundSplit = '#f8f8f8';

  @property({ type: String, attribute: 'pill-background-mixed' })
  accessor pillBackgroundMixed = '#ffffff';

  @property({ type: String, attribute: 'pill-hover-background' })
  accessor pillHoverBackground = '#C8DCF9';

  @property({ type: String, attribute: 'pill-border-split' })
  accessor pillBorderSplit = 'transparent';

  @property({ type: String, attribute: 'pill-border-mixed' })
  accessor pillBorderMixed = 'transparent';

  @property({ type: String, attribute: 'pill-text-color' })
  accessor pillTextColor = '#1456B8';

  @property({ type: String, attribute: 'ad-badge-color' })
  accessor adBadgeColor = '#1456B8';

  @property({ type: String, attribute: 'ad-badge-background' })
  accessor adBadgeBackground = 'transparent';

  // Internal state
  @state()
  accessor _contentQuestions = [];

  @state()
  accessor _sponsoredQuestions = [];

  @state()
  accessor _isLoadingContent = false;

  @state()
  accessor _isLoadingSponsored = false;

  @state()
  accessor _settingsOpen = false;

  @state()
  accessor _isValidated = false;

  // Tooltip state
  @state()
  accessor _tooltip = null; // { visible: boolean, text: string, x: number, y: number }

  // IntersectionObserver for tracking question pill visibility
  _questionVisibilityObserver = null;
  _tooltipHideTimeout = null;

  // Track which questions have already logged QUESTION_VISIBLE (deduplication)
  _loggedVisibleQuestions = new Set();

  // Tooltip hover tracking
  _tooltipHoverStartTime = null;
  _tooltipHoveredQuestion = null;
  _tooltipHoverThresholdMs = 300; // Minimum hover duration to count as intentional hover

  apiBaseUrl = __QUESTIONS_API_BASE_URL__;
  _platformConfigUrl = __PLATFORM_CONFIG_URL__;
  _platformConfig = null;

  // Feature flag cache: Map<flagKey, value>
  _featureFlagCache = new Map();
  // Per-flag background refresh timers: Map<flagKey, timerId>
  _featureFlagTimers = new Map();
  _redirectorBaseUrl = __QUESTIONS_REDIRECTOR_BASE_URL__;
  _prefetchedUrls = new Map();
  _prefetchPromises = new Map();
  _analytics = null;
  _settingsAnchorRef = null;
  _settingsDropdownRef = null;
  _widgetInitializedTime = null;
  _cachedBaseEventData = null;
  _cachedIpAddress = null;

  // Track page content and extraction timing for scroll-based updates
  _pageContentRef = '';
  _lastExtractionTimeRef = 0;
  _scrollThrottleMs = 3000; // Minimum time between extractions
  _contentGrowthThreshold = 1.15; // 15% growth threshold
  _scrollTimeout = null;
  _isProcessingScroll = false;
  _boundHandleScroll = null;

  // URL change detection for SPA support
  _currentUrl = '';
  _boundHandlePopState = null;
  _boundHandleHashChange = null;
  _originalPushState = null;
  _originalReplaceState = null;

  static styles = css`
    :host {
      display: block;
      width: 100%;
      font-family: Inter, 'Helvetica Neue', Helvetica, Arial, sans-serif;
      position: sticky;
      top: var(--gist-questions-top-offset, 0);
      z-index: var(--gist-questions-z-index, 9999);
      /* Stretch across the full viewport width including safe-area gutters so
         the bar background covers the notch/Dynamic Island zone edge-to-edge. */
      box-sizing: border-box;
    }

    :host([pinned]) {
      position: fixed;
      top: var(--gist-questions-top-offset, 0);
      left: 0;
      right: 0;
      z-index: 2147483647;
    }

    .gist-questions-bar {
      background: var(--gist-questions-bar-background, #ffffff);
      box-shadow: var(--gist-questions-bar-shadow, 0 1px 3px 0 rgba(0, 0, 0, 0.1));
      height: 48px;
      transition: all 0.3s ease-in-out;
    }

    .gist-questions-bar.settings-open {
      overflow: visible;
    }

    .gist-questions-bar:not(.settings-open) {
      overflow: hidden;
    }

    .gist-questions-bar-split {
      border-top: 0.5px solid black;
      box-shadow: inset 0 10px 22px -6px rgba(0, 0, 0, 0.3), 0 1px 3px 0 rgba(0, 0, 0, 0.1);
    }

    .bar-content {
      display: flex;
      /* Layer 1 — JS probe (works without viewport-fit=cover):
           --gist-safe-area-left is set by _applySafeAreaInset() at runtime.
         Layer 2 — CSS env() (works when host page has viewport-fit=cover).
         Layer 3 — 4px minimum so non-notch devices still have a small gutter. */
      padding-left:  max(var(--gist-safe-area-left, 4px), env(safe-area-inset-left, 4px));
      padding-right: max(var(--gist-safe-area-right, 4px), env(safe-area-inset-right, 4px));
      align-items: center;
      height: 100%;
    }

    /* Scrolling Section */
    .scrolling-section {
      flex: 1;
      overflow: hidden;
      position: relative;
      padding: 4px 8px;
      display: flex;
      align-items: center;
      gap: 8px;
      min-width: 0;
    }

    .scrolling-section.with-border {
      border-right: 1px solid #e5e7eb;
    }

    .section-label {
      display: flex;
      align-items: center;
      gap: 4px;
      flex-shrink: 0;
      font-size: 12px;
      font-weight: 500;
      color: #374151;
      white-space: nowrap;
      padding: 0 4px;
    }

    .section-icon {
      width: 16px;
      height: 16px;
    }

    .section-icon.blue {
      color: #2563eb;
    }

    .section-icon.purple {
      color: #9333ea;
    }

    .section-title {
      font-size: 12px;
      font-weight: 700;
      line-height: 1.25;
      white-space: nowrap;
    }

    .section-title.blue {
      color: #3b82f6;
    }

    .section-title.green {
      color: #16a34a;
    }

    /* Scrolling Content Wrapper */
    .scrolling-content-wrapper {
      flex: 1;
      /* Use 'clip' rather than 'hidden' so this element does not become a
         scroll container. Otherwise, tabbing to a question-pill that is
         currently translated out of view triggers the browser's automatic
         scrollIntoView, which shifts scrollLeft and visibly drags the
         ::before/::after gradient fades along with the content. */
      overflow: clip;
      position: relative;
      min-width: 0;
    }

    .scrolling-content-wrapper::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 8%;
      height: 100%;
      background: linear-gradient(
        90deg,
        var(--gist-questions-bar-background, #ffffff) 0%,
        transparent 100%
      );
      pointer-events: none;
      z-index: 1;
    }

    .scrolling-content-wrapper::after {
      content: '';
      position: absolute;
      top: 0;
      right: 0;
      width: 8%;
      height: 100%;
      background: linear-gradient(
        90deg,
        transparent 0%,
        var(--gist-questions-bar-background, #ffffff) 100%
      );
      pointer-events: none;
      z-index: 1;
    }

    /* Scrolling Content */
    .scrolling-content {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      flex-shrink: 0;
      animation: scroll-left-to-right 40s linear infinite paused;
      will-change: transform;
      position: relative;
      z-index: 0;
      font-size: 14px;
    }

    /* Split mode: scroll on hover */
    .gist-questions-bar-split:hover .scrolling-content {
      animation-play-state: running;
    }

    /* Mixed mode: scroll by default, pause on hover (pointer devices only — touch devices keep scrolling) */
    .gist-questions-bar-mixed .scrolling-content {
      animation-duration: 40s;
      animation-play-state: running;
    }

    @media (hover: hover) {
      .gist-questions-bar-mixed:hover .scrolling-content {
        animation-play-state: paused;
      }
    }

    /* Pause animation during loading state */
    .scrolling-content.loading {
      animation-play-state: paused !important;
    }

    @keyframes scroll-left-to-right {
      0% {
        transform: translateX(0%);
      }
      100% {
        transform: translateX(-33.33%);
      }
    }

    /* Question Pills */
    .question-pill {
      display: inline-flex;
      align-items: center;
      padding: 4px 12px;
      border-radius: 9999px;
      font-size: 14px;
      line-height: 1.2;
      cursor: pointer;
      white-space: nowrap;
      transition: all 0.2s;
      flex-shrink: 0;
    }

    /* Split mode pills - gradient designs */
    .gist-questions-bar-split .question-pill.related {
      border: 2px solid var(--gist-questions-pill-border-split, transparent);
      background: linear-gradient(var(--gist-questions-pill-background-split, #f8f8f8), var(--gist-questions-pill-background-split, #f8f8f8)) padding-box,
                  linear-gradient(to top right, #fbbf24, #fb923c, #ef4444, #a855f7, #3b82f6) border-box;
      color: var(--gist-questions-pill-text-color, #1456B8);
    }

    .gist-questions-bar-split .question-pill.sponsored {
      border: 2px solid var(--gist-questions-pill-border-split, transparent);
      background: linear-gradient(var(--gist-questions-pill-background-split, #f8f8f8), var(--gist-questions-pill-background-split, #f8f8f8)) padding-box,
                  linear-gradient(to top right, #93c5fd, #4ade80, #fbbf24, #fb923c) border-box;
      color: var(--gist-questions-pill-text-color, #1456B8);
    }

    /* Mixed mode pills - gradient border by default */
    .gist-questions-bar-mixed .question-pill {
      --_pill-bg: var(--gist-questions-pill-background-mixed, #ffffff);
      --_pill-border: var(--gist-questions-pill-border-mixed, transparent);
      /* Default gradient border: #C25D97 -> #FF6655 -> #6986F9 */
      background: var(--gist-questions-pill-border-gradient, linear-gradient(var(--_pill-bg), var(--_pill-bg)) padding-box,
                  linear-gradient(to right, #C25D97, #FF6655, #6986F9) border-box);
      border: 2px solid transparent;
      color: var(--gist-questions-pill-text-color, #1456B8);
    }

    .gist-questions-bar-mixed .question-pill:hover {
      --_pill-hover-bg: var(--gist-questions-pill-hover-background, #C8DCF9);
      /* Default gradient border on hover */
      background: var(--gist-questions-pill-border-gradient-hover, linear-gradient(var(--_pill-hover-bg), var(--_pill-hover-bg)) padding-box,
                  linear-gradient(to right, #C25D97, #FF6655, #6986F9) border-box);
    }

    /* Split mode pills hover - applies to both related and sponsored */
    .gist-questions-bar-split .question-pill.related:hover,
    .gist-questions-bar-split .question-pill.sponsored:hover {
      background: linear-gradient(var(--gist-questions-pill-hover-background, #C8DCF9), var(--gist-questions-pill-hover-background, #C8DCF9)) padding-box,
                  linear-gradient(to top right, #fbbf24, #fb923c, #ef4444, #a855f7, #3b82f6) border-box;
    }

    /* Mixed mode focus - higher specificity to override mixed mode border */
    .gist-questions-bar-mixed .question-pill:focus-visible {
      border-color: var(--gist-questions-pill-focus-border-color, #6A86F9);
      border-width: 2px;
      outline: none;
    }

    /* Split mode focus */
    .gist-questions-bar-split .question-pill:focus-visible {
      border-color: var(--gist-questions-pill-focus-border-color, #3b82f6) !important;
      border-width: 1px !important;
      outline: none;
    }

    /* AD Badge */
    .ad-badge {
      font-size: 10px;
      border-radius: 9999px;
      border: 1px solid var(--gist-questions-ad-badge-color, var(--gist-questions-pill-text-color, #1456B8));
      color: var(--gist-questions-ad-badge-color, var(--gist-questions-pill-text-color, #1456B8));
      background-color: var(--gist-questions-ad-badge-background, transparent);
      padding: 2px 4px;
      line-height: 1;
      margin-right: 6px;
    }

    /* Loading Spinner */
    .loading-spinner {
      display: inline-block;
      width: 16px;
      height: 16px;
      border: 2px solid #e5e7eb;
      border-top-color: #3b82f6;
      border-radius: 50%;
      animation: spin 1s linear infinite;
    }

    @keyframes spin {
      to {
        transform: rotate(360deg);
      }
    }

    .loading-text {
      font-size: 12px;
      color: #6b7280;
      white-space: nowrap;
    }

    /* Question Tooltip - positioned outside scrolling-section to avoid overflow:hidden clipping */
    .question-tooltip {
      position: fixed;
      z-index: 10000000;
      pointer-events: auto;
      transform: translateX(-50%);
      animation: tooltipFadeIn 0.15s ease-out;
      margin-top: 6px;
    }

    @keyframes tooltipFadeIn {
      from {
        opacity: 0;
        transform: translateX(-50%) translateY(-5px);
      }
      to {
        opacity: 1;
        transform: translateX(-50%) translateY(0);
      }
    }

    /* Tablet and below: 3-column layout with Related and Sponsored stacked in middle column */
    @media (max-width: 768px) {
      .gist-questions-bar-split {
        height: auto;
        min-height: 40px;
      }

      .gist-questions-bar-split .bar-content {
        display: grid;
        grid-template-columns: auto 1fr auto;
        grid-template-rows: auto auto;
        grid-template-areas:
          "logo related settings"
          "ad sponsored settings";
        align-items: center;
        height: auto;
        gap: 0;
      }


      /* AD label in the bottom-left area */
      .gist-questions-bar-split .bar-content::before {
        content: 'AD';
        grid-area: ad;
        justify-self: center;
        align-self: center;
        font-size: 11px;
        font-weight: 700;
        color: #16a34a;
        padding: 2px 8px;
      }

      .gist-questions-bar-split .settings-button {
        grid-area: settings;
        justify-self: center;
        align-self: center;
        position: static;
      }

      .gist-questions-bar-split .scrolling-section {
        flex: none;
        border-right: none;
        padding: 2px 0;
        min-height: 0;
        gap: 4px;
      }

      /* Related section goes to 'related' grid area */
      .gist-questions-bar-split .scrolling-section.with-border {
        grid-area: related;
        border-right: none;
        border-bottom: 1px solid #e5e7eb;
      }

      /* Sponsored section goes to 'sponsored' grid area */
      .gist-questions-bar-split .scrolling-section:not(.with-border) {
        grid-area: sponsored;
      }

      /* Compact pills in tablet mode */
      .gist-questions-bar-split .question-pill {
        padding: 2px 8px;
        font-size: 12px;
      }

      /* Faster horizontal scroll on smaller screens */
      .gist-questions-bar-split .scrolling-content {
        animation-duration: 30s;
      }

      .gist-questions-bar-mixed .scrolling-content {
        animation-duration: 40s;
      }

      .gist-questions-bar-split .section-label {
        padding: 0 2px;
        gap: 2px;
      }

      .gist-questions-bar-split .section-title {
        font-size: 11px;
        line-height: 1.2;
      }

      /* Hide section labels in split mode */
      .gist-questions-bar-split .scrolling-section .section-label {
        display: none;
      }

      /* Hide section labels in mixed mode */
      .gist-questions-bar-mixed .scrolling-section .section-label {
        display: none;
      }
    }

    .question-tooltip-content {
      background: #1f2937;
      color: white;
      padding: 8px 12px;
      border-radius: 6px;
      font-size: 12px;
      line-height: 1.4;
      max-width: 280px;
      word-wrap: break-word;
      box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
    }

    .question-tooltip-arrow {
      position: absolute;
      top: -6px;
      left: 50%;
      transform: translateX(-50%);
      width: 0;
      height: 0;
      border-left: 6px solid transparent;
      border-right: 6px solid transparent;
      border-bottom: 6px solid #1f2937;
    }

    /* Settings Button */
    .settings-button {
      position: relative;
      flex-shrink: 0;
    }

    .settings-button .settings-button-icon {
      padding: 6px;
      border-radius: 4px;
      background: transparent;
      border: none;
      cursor: pointer;
      color: #6b7280;
      transition: all 0.2s;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .settings-button .settings-button-icon:hover {
      background: #f3f4f6;
      color: #1f2937;
    }

    .settings-button .settings-button-icon svg {
      width: 20px;
      height: 20px;
    }

    /* Settings Dropdown */
    .settings-dropdown {
      position: absolute;
      right: 0;
      top: 100%;
      margin-top: 4px;
      z-index: 10001;
      width: 220px;
      max-height: 85vh;
      overflow: auto;
      background: white;
      border: 1px solid #e5e7eb;
      box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
      border-radius: 8px;
      padding: 16px;
    }

    .settings-header {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-bottom: 12px;
    }

    .settings-header svg {
      width: 20px;
      height: 20px;
      color: #374151;
    }

    .settings-header h2 {
      font-size: 14px;
      font-weight: 600;
      color: #111827;
      margin: 0;
    }

    /* Settings Form */
    .settings-form {
      display: flex;
      flex-direction: column;
      gap: 12px;
    }

    .form-section {
      display: flex;
      flex-direction: column;
      gap: 6px;
    }

    .form-section label {
      font-size: 12px;
      font-weight: 500;
      color: #374151;
    }

    .radio-group {
      display: flex;
      flex-direction: column;
      gap: 4px;
    }

    .radio-option {
      display: flex;
      align-items: center;
      gap: 6px;
      cursor: pointer;
      font-size: 12px;
      color: #374151;
    }

    .radio-option input[type="radio"] {
      margin: 0;
      accent-color: #2563eb;
    }

    /* Toggle Switch */
    .toggle-row {
      display: flex;
      align-items: center;
      justify-content: space-between;
    }

    .toggle-row label {
      font-size: 12px;
      font-weight: 500;
      color: #374151;
    }

    .toggle-row .toggle-switch {
      position: relative;
      display: inline-flex;
      align-items: center;
      width: 36px;
      height: 20px;
      border-radius: 9999px;
      background: #dc2626;
      cursor: pointer;
      transition: background 0.2s;
      border: none;
      padding: 0;
    }

    .toggle-switch.enabled {
      background: #16a34a;
    }

    .toggle-switch::after {
      content: '';
      position: absolute;
      left: 2px;
      width: 16px;
      height: 16px;
      border-radius: 50%;
      background: white;
      transition: transform 0.2s;
    }

    .toggle-switch.enabled::after {
      transform: translateX(16px);
    }

    /* Refresh Button */
    .refresh-button {
      width: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 6px;
      background: #2563eb;
      color: white;
      border: none;
      border-radius: 6px;
      padding: 8px 12px;
      font-size: 12px;
      font-weight: 500;
      cursor: pointer;
      transition: background 0.2s;
      margin-top: 8px;
    }

    .refresh-button:hover {
      background: #1d4ed8;
    }

    .refresh-button svg {
      width: 14px;
      height: 14px;
    }

    /* Hidden state */
    .hidden {
      display: none !important;
    }
  `;

  connectedCallback() {
    super.connectedCallback();
    preloadFingerprint();
    this._applySafeAreaInset();
    this._loadInterFont();
  }

  /**
   * Inject Inter font link into document head if not already present.
   * Fonts must be loaded at document level to work inside Shadow DOM.
   */
  _loadInterFont() {
    const fontUrl = 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600&display=swap';
    const existingLink = document.querySelector(`link[href="${fontUrl}"]`);
    if (!existingLink) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = fontUrl;
      document.head.appendChild(link);
    }
  }

  // Detects iOS safe-area inset (notch / Dynamic Island) via a temporary
  // probe element and applies the offset as left/right padding on .bar-content
  // using a CSS custom property. This works even when the host page does NOT
  // have viewport-fit=cover in its <meta viewport> tag.
  _applySafeAreaInset() {
    try {
      const probe = document.createElement('div');
      probe.style.cssText =
        'position:fixed;top:-999px;left:0;width:0;height:0;' +
        'padding-left:env(safe-area-inset-left,0px);' +
        'padding-right:env(safe-area-inset-right,0px);pointer-events:none;';
      document.body.appendChild(probe);
      const cs = window.getComputedStyle(probe);
      const left  = parseFloat(cs.paddingLeft)  || 0;
      const right = parseFloat(cs.paddingRight) || 0;
      document.body.removeChild(probe);

      if (left > 0 || right > 0) {
        logger.log('safe-area insets detected — left:', left, 'right:', right);
        this.style.setProperty('--gist-safe-area-left',  left  + 'px');
        this.style.setProperty('--gist-safe-area-right', right + 'px');
      }
    } catch (e) {
      // Non-critical — CSS fallback still applies
    }
  }

  mapApiKey() {
    if (this.apiKey === 'sp_key@1401f35a-3ef1-44a6-95b0-d213b1ad69a6') {
      this.apiKey = 'df693593119d49aa978ee0de1f282c4b';
    }
  }

  updated(changedProperties) {
    // Set CSS custom properties on host element only when explicitly changed via attribute
    // This allows themes to provide defaults while still allowing HTML attribute overrides
    if (changedProperties.has('topOffset') && this.topOffset !== '0') {
      this.style.setProperty('--gist-questions-top-offset', this.topOffset);
    }
    if (changedProperties.has('zIndexValue') && this.zIndexValue !== '9999') {
      this.style.setProperty('--gist-questions-z-index', this.zIndexValue);
    }
    if (changedProperties.has('barBackground') && this.barBackground !== '#ffffff') {
      this.style.setProperty('--gist-questions-bar-background', this.barBackground);
    }
    if (changedProperties.has('pillBackgroundSplit') && this.pillBackgroundSplit !== '#f8f8f8') {
      this.style.setProperty('--gist-questions-pill-background-split', this.pillBackgroundSplit);
    }
    if (changedProperties.has('pillBackgroundMixed') && this.pillBackgroundMixed !== '#ffffff') {
      this.style.setProperty('--gist-questions-pill-background-mixed', this.pillBackgroundMixed);
    }
    if (changedProperties.has('pillHoverBackground') && this.pillHoverBackground !== '#C8DCF9') {
      this.style.setProperty('--gist-questions-pill-hover-background', this.pillHoverBackground);
    }
    if (changedProperties.has('pillBorderSplit') && this.pillBorderSplit !== 'transparent') {
      this.style.setProperty('--gist-questions-pill-border-split', this.pillBorderSplit);
    }
    if (changedProperties.has('pillBorderMixed') && this.pillBorderMixed !== 'transparent') {
      this.style.setProperty('--gist-questions-pill-border-mixed', this.pillBorderMixed);
    }
    if (changedProperties.has('pillTextColor') && this.pillTextColor !== '#1456B8') {
      this.style.setProperty('--gist-questions-pill-text-color', this.pillTextColor);
    }
    if (changedProperties.has('adBadgeColor') && this.adBadgeColor !== '#1456B8') {
      this.style.setProperty('--gist-questions-ad-badge-color', this.adBadgeColor);
    }
    if (changedProperties.has('adBadgeBackground') && this.adBadgeBackground !== 'transparent') {
      this.style.setProperty('--gist-questions-ad-badge-background', this.adBadgeBackground);
    }
  }

  /**
   * Publishes an analytics event to the platform-config publishEvent endpoint.
   * Called automatically whenever Analytics.track() fires.
   * @param {string} eventName  - The analytics event name
   * @param {object} properties - Optional event properties
   */
  async _publishEvent(eventName, properties) {
    /*if (FEATURE_FLAG_DEFAULTS['is-feature-flag-enabled'] === false) {
      logger.log('_publishEvent skipped — is-feature-flag-enabled is false');
      return;
    }*/
    if (!this._platformConfigUrl) {
      console.error('[GistWidget] _publishEvent — PLATFORM_CONFIG_URL is not set');
      return;
    }
    try {
      const base = isStagingKey(this.apiKey)
          ? 'https://api-staging.prorata.ai/services/v1'
          : (this._platformConfigUrl.replace(/\/$/, '') || __PLATFORM_CONFIG_URL__);

      const url = `${base}/question-platform-config/publishEvent`;

      // Use pre-fetched base event data if available; otherwise fall back to live fetch
      const baseData = this._cachedBaseEventData
        || (amplitudeModule?.getBaseEventData ? await amplitudeModule.getBaseEventData() : {});

      // Map internal widget event names to the server's allowed event_name enum.
      // Events not in this map are dropped (not published) to avoid 422 errors.
      const EVENT_NAME_MAP = {
        'Questions Bar Widget Initialized':             'question_bar_loaded',
        'Questions Bar Widget Visible':                 'question_bar_widget_visible',
        'Questions Bar Widget Question Clicked':        'question_bar_widget_question_clicked',
        'Questions Bar Widget Question Visible':        'question_bar_widget_question_visible',
        'Questions Bar Widget Questions Refreshed':     'question_bar_widget_question_refreshed',
        'Questions Bar Widget Question Tooltip Hovered':'question_bar_widget_question_hover',
        'Questions Bar Widget Settings Changed':        'question_bar_widget_settings_changed',
        'Questions Bar Widget Page Close':              'question_bar_closed',
        'Questions Bar Widget API Call':                'question_bar_widget_api_call',
        'Questions Bar Widget API Error':               'question_bar_widget_api_error',
      };

      // Fall back to generic snake_case conversion so unmapped events still reach
      // the server — the server will return 422 for truly invalid names.
      const snakeEventName = EVENT_NAME_MAP[eventName]
        || (eventName || '').toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');

      const body = {
        addl_properties:        {},
        browser_name:           baseData.browser_name           || '',
        browser_version:        baseData.browser_version        || '',
        client_hints_supported: baseData.client_hints_supported ?? false,
        device_id:              baseData.device_id              || '',
        event_name:             snakeEventName,
        impression_id:          `imp_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
        ip_address:             this._cachedIpAddress           || '',
        language:               baseData.language               || navigator.language || '',
        os_name:                baseData.os_name                || '',
        os_version:             baseData.os_version             || '',
        page_url:               baseData.page_url               || window.location.href || '',
        platform:               'web',
        properties:             properties || {},
        publisher_name:         baseData.publisher_name         || 'PrtQuestionsWidget',
        referrer:               baseData.referrer_url           || document.referrer  || '',
        session_id:             baseData.session_id             || '',
        timestamp:              Date.now(),
        user_id:                this.userId                     || '',
        version:                __WIDGET_VERSION__,
        visitor_id:             baseData.visitor_id             || '',
      };

      logger.log('_publishEvent —', snakeEventName, body);

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': this.apiKey,
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        console.error('[GistWidget] _publishEvent — HTTP', response.status, response.statusText, '| url:', url);
        return;
      }

      this._platformConfig = await response.json();
      logger.log('[GistWidget] _publishEvent — response:', this._platformConfig);
    } catch (err) {
      console.error('[GistWidget] _publishEvent — fetch error:', err);
    }
  }

  /**
   * Fetches a single feature flag value from the platform-config service.
   *
   *   GET {platformConfigUrl}/question-platform-config/api/features/flag/{flagKey}
   *   Header: Ocp-Apim-Subscription-Key: <apiKey>
   *
   * Behaviour:
   *  - On success  → stores the value in _featureFlagCache and returns it.
   *  - On failure  → returns the last cached value, or the default from
   *                  FEATURE_FLAG_DEFAULTS, or undefined if neither exists.
   *  - Call _startFeatureFlagPolling(flagKey) to keep the cache fresh.
   */
  async _fetchFeatureFlag(flagKey) {
    if (FEATURE_FLAG_DEFAULTS['is-feature-flag-enabled'] === false) {
      logger.info('feature flag is disabled');
      return false;
    }
    if (!this._platformConfigUrl) {
      logger.error('_fetchFeatureFlag — PLATFORM_CONFIG_URL is not set');
      return this._featureFlagCache.get(flagKey) ?? FEATURE_FLAG_DEFAULTS[flagKey];
    }
    try {
      const base = this._platformConfigUrl.replace(/\/$/, '');
      const url = `${base}/question-platform-config/api/features/flag/${encodeURIComponent(flagKey)}`;

      logger.log('_fetchFeatureFlag — fetching', flagKey, '|', url);

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': this.apiKey,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status} ${response.statusText}`);
      }

      const data = await response.json();

      // If the server reports the flag was not found, fall back to the config default
      if (data?.found === false) {
        const def = FEATURE_FLAG_DEFAULTS[flagKey];
        logger.warn(`_fetchFeatureFlag — flag "${flagKey}" not found on server, using default:`, def);
        this._featureFlagCache.set(flagKey, def);
        return def;
      }

      // Response shape: { flag, enabled, value, found, source, evaluated_with_attributes }
      // Use the "value" field; fall back to "enabled" for simpler responses; then the raw payload.
      // A null/undefined response value falls back to the config default (false for amplitude-flag).
      const rawValue = data?.value ?? data?.enabled ?? data;
      const value = rawValue !== null && rawValue !== undefined
        ? rawValue
        : (FEATURE_FLAG_DEFAULTS[flagKey] ?? false);

      this._featureFlagCache.set(flagKey, value);
      logger.log(
        `feature flag "${flagKey}" = ${value}`,
        `| enabled: ${data?.enabled} | source: ${data?.source} | found: ${data?.found}`
      );
      return value;
    } catch (err) {
      logger.error('_fetchFeatureFlag — fetch error for', flagKey, ':', err);
      // Retain last cached value; fall back to config default; then undefined
      if (this._featureFlagCache.has(flagKey)) {
        return this._featureFlagCache.get(flagKey);
      }
      const def = FEATURE_FLAG_DEFAULTS[flagKey];
      if (def !== undefined) {
        this._featureFlagCache.set(flagKey, def);
      }
      return def;
    }
  }

  /**
   * Returns the current cached value for a feature flag.
   * Falls back to FEATURE_FLAG_DEFAULTS if the flag has not been fetched yet.
   */
  getFeatureFlag(flagKey) {
    if (this._featureFlagCache.has(flagKey)) {
      return this._featureFlagCache.get(flagKey);
    }
    return FEATURE_FLAG_DEFAULTS[flagKey];
  }

  /**
   * Starts a background polling loop for the given flag key.
   * The first fetch runs immediately; subsequent fetches run every
   * FEATURE_FLAG_REFRESH_INTERVAL_MS milliseconds.
   * Calling this twice for the same key is a no-op.
   */
  _startFeatureFlagPolling(flagKey) {
    if(FEATURE_FLAG_DEFAULTS['is-feature-flag-enabled'] === false) {
      logger.info('feature flag is disabled');
      return;
    }
    if (this._featureFlagTimers.has(flagKey)) return; // already polling

    const poll = async () => {
      await this._fetchFeatureFlag(flagKey);
      const timerId = setTimeout(poll, FEATURE_FLAG_REFRESH_INTERVAL_MS);
      this._featureFlagTimers.set(flagKey, timerId);
    };

    // First fetch is immediate (non-blocking — caller does not await)
    poll();
  }

  firstUpdated() {
    // Validate required attributes
    // removing user-id as required field in the initial version
    if (!this.apiKey) {
      logger.error('QuestionsBarWidget requires api-key attribute');
      return;
    }

    this.mapApiKey()

    this._isValidated = true;

    // Start polling feature flags in the background
    if(FEATURE_FLAG_DEFAULTS['is-feature-flag-enabled'] === true) {
      this._startFeatureFlagPolling(__FEATURE_FLAG_KEY__);
      this._startFeatureFlagPolling('amplitude-flag');
    }

    // Initialize analytics
    this._initializeAnalytics();

    // Fire initialization event (in-memory tracking only, so it fires on every page load)
    if (!this._widgetInitEventFired) {
      Analytics.track(QUESTIONS_BAR_ANALYTICS_EVENTS.QUESTIONS_BAR_WIDGET_INITIALIZED);
      this._widgetInitEventFired = true;
    }

    // Load questions after a short delay to let page settle
    setTimeout(() => {
      const initialContent = extractPageContent();
      this._pageContentRef = initialContent;
      this._lastExtractionTimeRef = Date.now();
      this._currentUrl = window.location.href;
      this._loadQuestions(initialContent);

      // Setup scroll detection only after initial content is established
      // This prevents false content-growth triggers during initial load
      this._setupScrollDetection();
    }, 1000);

    // Setup click outside listener for settings
    document.addEventListener('click', this._handleClickOutside.bind(this));

    // Track widget initialization time for dwell time calculation
    this._widgetInitializedTime = Date.now();

    // Pre-fetch and cache base event data for synchronous page close tracking
    this._prefetchBaseEventData();

    // Setup page close tracking
    this._boundHandlePageClose = this._handlePageClose.bind(this);
    window.addEventListener('beforeunload', this._boundHandlePageClose);

    // Setup URL change detection for SPA support
    this._setupUrlChangeDetection();
  }

  /**
   * Setup scroll-based content update detection
   * Only called after initial content is loaded to avoid false triggers
   */
  _setupScrollDetection() {
    this._boundHandleScroll = this._handleScroll.bind(this);
    window.addEventListener('scroll', this._boundHandleScroll, { passive: true });
  }

  /**
   * Setup URL change detection for Single Page Applications (SPAs)
   * Detects navigation via history API and hash changes
   */
  _setupUrlChangeDetection() {
    // Note: We intentionally do NOT set this._currentUrl in _setupUrlChangeDetection().
    // It's set in the setTimeout before _loadQuestions() to:
    // - Prevent duplicate question loads if SPA navigates during initial API call
    // - Distinguish regular page load (no previous URL) from SPA navigation

    // Bind handlers
    this._boundHandlePopState = this._handleUrlChange.bind(this);
    this._boundHandleHashChange = this._handleUrlChange.bind(this);

    // Listen to popstate (back/forward navigation)
    window.addEventListener('popstate', this._boundHandlePopState);

    // Listen to hashchange (hash-based routing)
    window.addEventListener('hashchange', this._boundHandleHashChange);

    // Patch history.pushState and replaceState to detect programmatic navigation
    this._originalPushState = history.pushState;
    this._originalReplaceState = history.replaceState;

    const self = this;

    history.pushState = function(...args) {
      self._originalPushState.apply(this, args);
      self._handleUrlChange();
    };

    history.replaceState = function(...args) {
      self._originalReplaceState.apply(this, args);
      self._handleUrlChange();
    };
  }

  /**
   * Cleanup URL change detection listeners and patches
   */
  _cleanupUrlChangeDetection() {
    // Remove event listeners
    if (this._boundHandlePopState) {
      window.removeEventListener('popstate', this._boundHandlePopState);
    }
    if (this._boundHandleHashChange) {
      window.removeEventListener('hashchange', this._boundHandleHashChange);
    }

    // Restore original history methods
    if (this._originalPushState) {
      history.pushState = this._originalPushState;
    }
    if (this._originalReplaceState) {
      history.replaceState = this._originalReplaceState;
    }
  }

  /**
   * Handle URL changes - reload questions when URL changes in SPA
   */
  _handleUrlChange() {
    const newUrl = window.location.href;

    // Only reload if URL actually changed
    if (newUrl !== this._currentUrl) {
      console.log(`[QuestionsBarWidget] URL changed from ${this._currentUrl} to ${newUrl}, reloading questions`);

      // Track the URL change refresh only if this is an actual SPA navigation
      // (not the initial page load - we check if previous URL was set)
      if (this._currentUrl) {
        Analytics.track(QUESTIONS_BAR_ANALYTICS_EVENTS.QUESTIONS_REFRESHED, {
          refresh_reason: 'url_change',
          previous_url: this._currentUrl,
          new_url: newUrl,
          previous_questions_count: this._contentQuestions.length + this._sponsoredQuestions.length
        });
      }

      this._currentUrl = newUrl;

      // Reset content reference to force reload
      this._pageContentRef = '';
      this._lastExtractionTimeRef = 0;

      // Small delay to let the new page content settle
      setTimeout(() => {
        const newContent = extractPageContent();
        this._pageContentRef = newContent;
        this._lastExtractionTimeRef = Date.now();
        this._loadQuestions(newContent, true);
      }, 500);
    }
  }

  /**
   * Pre-fetch base event data and IP address for synchronous page close tracking.
   * This ensures data is available when the beforeunload handler fires.
   */
  async _prefetchBaseEventData() {
    try {
      if (amplitudeModule && amplitudeModule.getBaseEventData) {
        this._cachedBaseEventData = await amplitudeModule.getBaseEventData();
        this._cachedIpAddress = await amplitudeModule.getIPAddress?.() || 'unknown';
      }
    } catch (err) {
      // Silently fail - we'll track without pre-fetched data
      logger.warn('Failed to prefetch base event data:', err);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('click', this._handleClickOutside.bind(this));

    // Remove page close listener
    if (this._boundHandlePageClose) {
      window.removeEventListener('beforeunload', this._boundHandlePageClose);
    }

    // Remove scroll listener and clear timeout
    if (this._boundHandleScroll) {
      window.removeEventListener('scroll', this._boundHandleScroll);
    }
    if (this._scrollTimeout) {
      clearTimeout(this._scrollTimeout);
    }

    // Cleanup URL change detection
    this._cleanupUrlChangeDetection();

    // Clear all feature-flag background refresh timers
    for (const timerId of this._featureFlagTimers.values()) {
      clearTimeout(timerId);
    }
    this._featureFlagTimers.clear();

    // Disconnect question visibility observer
    if (this._questionVisibilityObserver) {
      this._questionVisibilityObserver.disconnect();
      this._questionVisibilityObserver = null;
    }
  }

  _handlePageClose() {
    if (!this._widgetInitializedTime) return;

    // Prevent duplicate page close events (in-memory only, so it fires on each page unload)
    if (this._widgetPageCloseEventFired) {
      return;
    }
    this._widgetPageCloseEventFired = true;

    const dwellTimeMs = Date.now() - this._widgetInitializedTime;
    const dwellTimeSeconds = Math.floor(dwellTimeMs / 1000);

    // Use pre-fetched data for synchronous tracking if available
    if (amplitudeModule && this._cachedBaseEventData) {
      amplitudeModule.trackPageCloseEvent(
        this._cachedBaseEventData,
        dwellTimeSeconds,
        dwellTimeMs,
        this._cachedIpAddress
      );
    } else {
      // Fallback to async tracking (may not complete before page closes)
      Analytics.track(QUESTIONS_BAR_ANALYTICS_EVENTS.PAGE_CLOSE, {
        dwell_time_seconds: dwellTimeSeconds,
        dwell_time_ms: dwellTimeMs
      });
    }
  }

  /**
   * Scroll-based content update detection
   * Reloads questions when page content grows by 15%+ on scroll
   */
  _handleScroll() {
    // Throttle: don't extract more than once every _scrollThrottleMs
    const now = Date.now();
    if (now - this._lastExtractionTimeRef < this._scrollThrottleMs) return;
    if (this._isProcessingScroll) return;

    clearTimeout(this._scrollTimeout);
    this._scrollTimeout = setTimeout(() => {
      this._isProcessingScroll = true;
      const newContent = extractPageContent();
      const currentLength = this._pageContentRef.length;
      const newLength = newContent.length;

      // Only reload if content grew meaningfully (15%+ new content)
      if (newLength > currentLength * this._contentGrowthThreshold) {
        logger.log(`Content grew from ${currentLength} to ${newLength} chars, fetching new questions`);
        Analytics.track(QUESTIONS_BAR_ANALYTICS_EVENTS.QUESTIONS_REFRESHED, {
          refresh_reason: 'scroll_content_update',
          previous_questions_count: this._contentQuestions.length + this._sponsoredQuestions.length
        });
        this._loadQuestions(newContent);
      }

      this._lastExtractionTimeRef = Date.now();
      this._isProcessingScroll = false;
    }, 500); // Wait for scroll to settle
  }

  _initializeAnalytics() {
    try {
      if (!isAnalyticsInitialized) {
        // Use the Amplitude API key injected at build time
        const amplitudeApiKey = typeof __AMPLITUDE_API_KEY__ !== 'undefined' ? __AMPLITUDE_API_KEY__ : '';

        Analytics.init({
          apiKey: this.apiKey,
          userId: this.userId,
          widgetType: 'questions',
          apiBaseUrl: this.apiBaseUrl,
          amplitudeApiKey: amplitudeApiKey
        });

        // Inject Amplitude module for bundling control (for page close tracking)
        // This ensures Amplitude SDK is only bundled with widgets that import it
        if (amplitudeApiKey && amplitudeModule) {
          Analytics.setAmplitudeModule(amplitudeModule);
        }

        isAnalyticsInitialized = true;

        // Wrap Analytics.track once so every event also calls _publishEvent
        // _publishEvent is only invoked when the "amplitude-flag" feature flag is true
        const originalTrack = Analytics.track.bind(Analytics);
        Analytics.track = (eventName, properties, ...rest) => {
          originalTrack(eventName, properties, ...rest);
          /*if (this.getFeatureFlag('amplitude-flag') === true) {*/
          this._publishEvent(eventName, properties);
          /*} else {
            logger.log('_publishEvent skipped — amplitude-flag is not enabled');
          }*/
        };
      }
    } catch (error) {
      logger.error('Failed to initialize analytics:', error);
    }
  }

  async _loadQuestions(providedContent, isManualRefresh = false) {
    if (this.disabled) return;

    try {
      const content = providedContent || extractPageContent();

      // Skip if no content
      if (!content.trim()) return;

      // Check if content has grown meaningfully (for scroll-based lazy loading)
      const currentLength = this._pageContentRef.length;
      const newLength = content.length;
      const hasContentGrown = newLength > currentLength * this._contentGrowthThreshold;

      // If content hasn't grown and we already have questions, skip (unless manual refresh)
      if (!hasContentGrown && this._contentQuestions.length > 0 && !isManualRefresh) {
        return;
      }

      // Update stored content
      this._pageContentRef = content;

      // Reset visible question tracking for new page context
      this._loggedVisibleQuestions.clear();

      if (content.trim()) {
        // Generate a unique page request ID to correlate related + sponsored API calls
        const pageRequestId = `${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;

        // Start loading both types of questions in parallel
        this._isLoadingContent = true;
        this._isLoadingSponsored = true;

        // Fetch content questions first (they're faster)
        const contentPromise = fetchQuestions(this.apiBaseUrl, this.apiKey, content, undefined, this.pageUrl, 'content', pageRequestId)
          .then(questions => {
            this._contentQuestions = questions;
            this._isLoadingContent = false;

            return questions;
          })
          .catch(error => {
            logger.error('Failed to load content questions:', error);
            this._isLoadingContent = false;
            return [];
          });

        // Fetch sponsored questions (they take longer)
        const sponsoredPromise = fetchQuestions(this.apiBaseUrl, this.apiKey, content, undefined, this.pageUrl, 'sponsored', pageRequestId)
          .then(questions => {
            this._sponsoredQuestions = questions;
            this._isLoadingSponsored = false;
            return questions;
          })
          .catch(error => {
            logger.error('Failed to load sponsored questions:', error);
            this._isLoadingSponsored = false;
            return [];
          });

        // Wait for both to complete
        const [contentResult, sponsoredResult] = await Promise.all([contentPromise, sponsoredPromise]);

        // Wait for Lit to finish rendering with the new questions before setting up visibility tracking
        await this.updateComplete;

        // Setup visibility tracking (fires when widget is actually on screen)
        this._setupVisibilityTracking(contentResult.length, sponsoredResult.length);
      }
    } catch (error) {
      logger.error('Failed to load questions:', error);
      this._isLoadingContent = false;
      this._isLoadingSponsored = false;
    }
  }

  _setupVisibilityTracking(contentCount, sponsoredCount) {
    // Use in-memory only tracking so event fires on every page load
    // (sessionStorage persists across reloads which prevents re-logging)
    if (this._widgetVisibleEventFired) {
      return;
    }

    const totalQuestions = contentCount + sponsoredCount;
    let observer = null;

    const trackVisible = (intersectionRatio) => {
      Analytics.track(QUESTIONS_BAR_ANALYTICS_EVENTS.QUESTIONS_BAR_WIDGET_VISIBLE, {
        questions_loaded: totalQuestions,
        content_questions_count: contentCount,
        sponsored_questions_count: sponsoredCount,
        display_mode: this.displayMode,
        mix_pattern: this.mixPattern,
        intersection_ratio: intersectionRatio
      });
      this._widgetVisibleEventFired = true;
      if (observer) {
        observer.disconnect();
      }
    };

    // Use IntersectionObserver to track when widget becomes visible
    observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          trackVisible(entry.intersectionRatio);
        }
      });
    }, { threshold: 0.5 });

    const widget = this.shadowRoot.querySelector('.gist-questions-bar');
    if (!widget) {
      return;
    }

    // Wait for layout to settle before observing to ensure accurate intersection detection
    requestAnimationFrame(() => {
      // Double-check we haven't already fired (in case of rapid successive calls)
      if (this._widgetVisibleEventFired) {
        return;
      }

      observer.observe(widget);

      // Also check if widget is already visible (IntersectionObserver should fire immediately,
      // but in some cases with delayed layout, it might not)
      const rect = widget.getBoundingClientRect();
      const windowHeight = window.innerHeight || document.documentElement.clientHeight;
      const windowWidth = window.innerWidth || document.documentElement.clientWidth;

      const isInViewport = (
        rect.top < windowHeight &&
        rect.bottom > 0 &&
        rect.left < windowWidth &&
        rect.right > 0 &&
        rect.width > 0 &&
        rect.height > 0
      );

      if (isInViewport) {
        trackVisible(1.0);
      }
    });
  }

  /**
   * Lit ref directive to observe question pill elements for visibility tracking
   * @param {Object} question - The question object
   * @param {string} uniqueId - Unique identifier for this pill instance
   * @returns {Function} Ref callback function
   */
  _observeQuestionPill(question, uniqueId) {
    return (element) => {
      if (!element) return;
      this._setupQuestionVisibilityTracking(element, question, uniqueId);
    };
  }

  /**
   * Set up IntersectionObserver for a question pill element
   * @param {HTMLElement} element - The question pill element
   * @param {Object} question - The question data
   * @param {string} uniqueId - Unique identifier for this pill instance
   */
  _setupQuestionVisibilityTracking(element, question, uniqueId) {
    // Create observer if not already created
    if (!this._questionVisibilityObserver) {
      this._questionVisibilityObserver = new IntersectionObserver(
        (entries) => this._handleQuestionVisibilityEntries(entries),
        { threshold: 0.5 }
      );
    }

    // Store question data on the element for retrieval in the callback
    element._questionData = {
      id: question.id,
      text: question.text,
      type: question.type,
      url: question.url,
      uniqueId: uniqueId
    };

    this._questionVisibilityObserver.observe(element);
  }

  /**
   * Handle IntersectionObserver entries for question pills
   * @param {IntersectionObserverEntry[]} entries - Observer entries
   */
  _handleQuestionVisibilityEntries(entries) {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        const element = entry.target;
        const questionData = element._questionData;

        if (questionData) {
          // Use question ID as the deduplication key
          const questionKey = questionData.id;

          // Only log if we haven't already tracked this question as visible
          if (!this._loggedVisibleQuestions.has(questionKey)) {
            this._loggedVisibleQuestions.add(questionKey);

            Analytics.track(QUESTIONS_BAR_ANALYTICS_EVENTS.QUESTION_VISIBLE, {
              question_id: questionData.id,
              question_text: questionData.text,
              question_type: questionData.type,
              display_mode: this.displayMode,
              intersection_ratio: entry.intersectionRatio,
              pill_unique_id: questionData.uniqueId
            });
          }
        }
      }
    });
  }

  // Fire the redirector request as soon as the user hovers/touches a pill and
  // cache the resolved URL. By the time the user actually clicks (300–500 ms
  // later on desktop, or even sooner on a quick tap), the URL is ready and we
  // can call openLink() synchronously — no blank-tab tricks, no async context
  // loss, works in Safari iOS.
  // Returns a Promise<string|null> that resolves to the redirector URL for this
  // question (or null if the redirector returns nothing usable / errors out).
  // The same Promise is returned for every subsequent call for the same question
  // so callers can await it without firing duplicate network requests.
  _prefetchRedirectorUrl(question) {
    // Sponsored questions always have a direct URL — no redirector needed.
    if (question.url || question.type === 'sponsored') return Promise.resolve(null);

    // Already resolved — return the cached value wrapped in a Promise.
    if (this._prefetchedUrls.has(question.id)) {
      const cached = this._prefetchedUrls.get(question.id);
      logger.log('prefetch skip (already cached) — id:', question.id, '| cached:', cached);
      return Promise.resolve(cached);
    }

    // In-flight — return the existing Promise so the caller awaits the same fetch.
    if (this._prefetchPromises.has(question.id)) {
      logger.log('prefetch in-flight — id:', question.id, '(reusing existing promise)');
      return this._prefetchPromises.get(question.id);
    }

    logger.log('prefetch START — id:', question.id, '| text:', question.text);

    const redirectorBaseUrl = isStagingKey(this.apiKey)
        ? 'https://api-staging.prorata.ai/services/v1/question-redirect-svc'
        : (this._redirectorBaseUrl || __QUESTIONS_REDIRECTOR_BASE_URL__);

    const endpoint = `${redirectorBaseUrl}/question_redirector/redirectToUrl`;

    const promise = fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-API-Key': this.apiKey },
      body: JSON.stringify({
        question_id: question.id,
        text: question.text,
        type: question.type,
        url: window.location.href,
        answerProvider: this.answerProvider,
        metadata: { context: question.context },
        is_default_url: question.isDefaultUrl
      }),
    }).then(async response => {
      if (!response.ok) {
        logger.error('prefetch FAILED — HTTP', response.status, '| id:', question.id);
        Analytics.track(QUESTIONS_BAR_ANALYTICS_EVENTS.API_ERROR, {
          error_message: `HTTP ${response.status} - ${response.statusText}`,
          api_endpoint: endpoint,
          question_type: question.type,
          question_id: question.id,
          page_url: window.location.href || '',
        });
        this._prefetchedUrls.set(question.id, null);
        return null;
      }
      const { redirect_url } = await response.json();
      logger.log('prefetch response — id:', question.id, '| redirect_url:', redirect_url);
      try {
        const parsed = new URL(redirect_url);
        if (parsed.protocol === 'https:') {
          // Text-fragment highlighting temporarily disabled
          // const textFragment = parseTextFragment(redirect_url);
          const href = buildHref(parsed.origin + parsed.pathname + parsed.search, null);
          logger.log('prefetch CACHED — id:', question.id, '| href:', href);
          this._prefetchedUrls.set(question.id, href);
          return href;
        }
        logger.warn('prefetch — non-https URL ignored:', redirect_url);
        Analytics.track(QUESTIONS_BAR_ANALYTICS_EVENTS.API_ERROR, {
          error_message: `Non-https URL received: ${redirect_url}`,
          api_endpoint: endpoint,
          question_type: question.type,
          question_id: question.id,
          page_url: window.location.href || '',
        });
        this._prefetchedUrls.set(question.id, null);
        return null;
      } catch (err) {
        logger.error('prefetch — URL parse error:', err, '| redirect_url:', redirect_url);
        Analytics.track(QUESTIONS_BAR_ANALYTICS_EVENTS.API_ERROR, {
          error_message: `URL parse error: ${err instanceof Error ? err.message : 'Unknown error'}`,
          api_endpoint: endpoint,
          question_type: question.type,
          question_id: question.id,
          page_url: window.location.href || '',
        });
        this._prefetchedUrls.set(question.id, null);
        return null;
      }
    }).catch((err) => {
      logger.error('prefetch — fetch error:', err, '| id:', question.id);
      Analytics.track(QUESTIONS_BAR_ANALYTICS_EVENTS.API_ERROR, {
        error_message: err instanceof Error ? err.message : 'Unknown fetch error',
        api_endpoint: endpoint,
        question_type: question.type,
        question_id: question.id,
        page_url: window.location.href || '',
      });
      this._prefetchedUrls.set(question.id, null);
      return null;
    });

    this._prefetchPromises.set(question.id, promise);
    return promise;
  }

  _handleQuestionClick(question) {
    logger.log('_handleQuestionClick fired —',
      'id:', question.id,
      '| text:', question.text,
      '| type:', question.type,
      '| url:', question.url);

    // On touch-only devices the browser fires a synthetic mouseenter immediately
    // before click (same gesture), so dismissing here would prevent the user from
    // ever seeing the tooltip. Let it stay and be dismissed by the 5-second timer
    // or by the touchstart-outside handler. On pointer devices, dismiss right away.
    const isTouchOnly = window.matchMedia('(hover: none)').matches;
    if (!isTouchOnly) {
      if (this._tooltipAutoHideTimeout) { clearTimeout(this._tooltipAutoHideTimeout); this._tooltipAutoHideTimeout = null; }
      if (this._tooltipHideTimeout)     { clearTimeout(this._tooltipHideTimeout);     this._tooltipHideTimeout = null; }
      this._tooltip = null;
    }

    let url = question.url;

    // Build answer-provider fallback URL for this question (used when the
    // redirector is unavailable, returns an error, or returns no usable URL).
    const fallbackUrl = this._buildFallbackUrl();
    logger.log('fallbackUrl:', fallbackUrl,
      '| LOCAL_REDIRECTOR_ENABLED:', __LOCAL_REDIRECTOR_ENABLED__,
      '| answerProvider:', this.answerProvider);

    if (__LOCAL_REDIRECTOR_ENABLED__ === null || __LOCAL_REDIRECTOR_ENABLED__ === true) {
      if (question.type === 'related') {
        url = fallbackUrl;
      } else {
        // For sponsored questions, use the existing URL from the API
        url = question.url;
      }

      // Track click for LOCAL_REDIRECTOR path
      Analytics.track(QUESTIONS_BAR_ANALYTICS_EVENTS.QUESTION_CLICKED, {
        question_id: question.id,
        question_text: question.text,
        question_type: question.type,
        question_url: url,
        answer_provider: this.answerProvider
      });

      logger.log('LOCAL_REDIRECTOR path — openLink:', url);
      openLink(url);

    } else {
      // Call redirector if no URL, or if isDefaultUrl is true (need redirector to resolve final URL)
      if (!url || question.isDefaultUrl) {
        const cachedUrl = this._prefetchedUrls.get(question.id);
        const isCacheReady = this._prefetchedUrls.has(question.id) && cachedUrl !== undefined;

        logger.log('redirector path — cache state:',
          !this._prefetchedUrls.has(question.id) ? 'MISS (never hovered)'  :
          cachedUrl                               ? 'HIT — ' + cachedUrl    :
                                                   'null (redirector had no result)');

        if (isCacheReady) {
          // ── Fast path: redirector result already available ─────────────────
          // Use it (or fallback if redirector returned null) synchronously so
          // openLink() runs inside the user-gesture context — Safari-safe.
          const finalUrl = cachedUrl || fallbackUrl;
          logger.log('opening (cache hit):', finalUrl);

          // Track click with the resolved redirect_url
          Analytics.track(QUESTIONS_BAR_ANALYTICS_EVENTS.QUESTION_CLICKED, {
            question_id: question.id,
            question_text: question.text,
            question_type: question.type,
            question_url: finalUrl,
            answer_provider: this.answerProvider
          });

          openLink(finalUrl);

        } else {
          // ── Slow path: fetch not yet started or still in flight ────────────
          // Open a blank tab RIGHT NOW while we are still inside the synchronous
          // click handler (preserves user-gesture context for window.open).
          // Then await the redirector promise and navigate the tab once it resolves.
          logger.log('no cache — opening blank tab and awaiting redirector fetch');
          const newTab = window.open('', '_blank');

          // Start (or reuse an already in-flight) redirector fetch.
          this._prefetchRedirectorUrl(question).then(href => {
            const finalUrl = href || fallbackUrl;
            logger.log('redirector resolved — navigating tab to:', finalUrl);

            // Track click with the resolved redirect_url
            Analytics.track(QUESTIONS_BAR_ANALYTICS_EVENTS.QUESTION_CLICKED, {
              question_id: question.id,
              question_text: question.text,
              question_type: question.type,
              question_url: finalUrl,
              answer_provider: this.answerProvider
            });

            if (newTab) {
              newTab.location.href = finalUrl;
            } else {
              // Popup was blocked; best-effort via openLink (gesture is gone,
              // but at least the user gets a console error explaining why).
              logger.error('blank tab was blocked by popup blocker — cannot navigate to:', finalUrl);
              openLink(finalUrl);
            }
          });
        }
      } else {
        // Track click for questions with direct URL (isDefaultUrl is false)
        Analytics.track(QUESTIONS_BAR_ANALYTICS_EVENTS.QUESTION_CLICKED, {
          question_id: question.id,
          question_text: question.text,
          question_type: question.type,
          question_url: url,
          answer_provider: this.answerProvider
        });

        logger.log('redirector path — question has direct url (isDefaultUrl=false):', url);
        openLink(url);
      }
    }
  }

  /**
   * Handle keyboard events on question pills for accessibility.
   * Activates the pill on Enter or Space key press.
   */
  _handleQuestionKeydown(event, question) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this._handleQuestionClick(question);
    }
  }

  // Build the answer-provider URL for a question text. This is used both as
  // the primary destination when LOCAL_REDIRECTOR_ENABLED is true and as the
  // fallback when the redirector is unavailable or returns no usable URL.
  _buildFallbackUrl() {
    // Fall back to the homepage (origin) of the page the widget is embedded on.
    // Prefer the canonical page-url attribute; fall back to window.location.
    try {
      const source = this.pageUrl || window.location.href;
      return new URL(source).origin + '/';
    } catch {
      return window.location.origin + '/';
    }
  }

  _handleSettingChange(key, value) {
    const oldValue = this[key];
    this[key] = value;

    // Track settings change
    Analytics.track(QUESTIONS_BAR_ANALYTICS_EVENTS.SETTINGS_CHANGED, {
      setting: key,
      old_value: oldValue,
      new_value: value
    });

    // Re-fetch questions if display mode changes to ensure proper mixing
    if (key === 'displayMode' || key === 'mixPattern') {
      this.requestUpdate();
    }

    // Dispatch custom event for parent to listen
    this.dispatchEvent(new CustomEvent('settings-changed', {
      detail: { key, value, oldValue },
      bubbles: true,
      composed: true
    }));
  }

  _handleRefresh() {
    const previousCount = this._contentQuestions.length + this._sponsoredQuestions.length;

    Analytics.track(QUESTIONS_BAR_ANALYTICS_EVENTS.QUESTIONS_REFRESHED, {
      refresh_reason: 'manual_refresh',
      previous_questions_count: previousCount
    });

    // Pass true for isManualRefresh to force reload regardless of content growth
    this._loadQuestions(undefined, true);
    this._settingsOpen = false;
  }

  _toggleSettings(e) {
    e.stopPropagation();
    this._settingsOpen = !this._settingsOpen;
  }

  _handleClickOutside(e) {
    if (!this._settingsOpen) return;

    const path = e.composedPath();
    const settingsButton = this.shadowRoot.querySelector('.settings-button');
    const settingsDropdown = this.shadowRoot.querySelector('.settings-dropdown');

    if (settingsButton && settingsDropdown) {
      const isInsideButton = settingsButton.contains(e.target) || path.includes(settingsButton);
      const isInsideDropdown = settingsDropdown.contains(e.target) || path.includes(settingsDropdown);

      if (!isInsideButton && !isInsideDropdown) {
        this._settingsOpen = false;
      }
    }
  }

  _getMixedQuestions() {
    const relatedQuestions = this._contentQuestions || [];
    const sponsoredQuestions = this._sponsoredQuestions || [];

    if (this.mixPattern === 'random') {
      return [...relatedQuestions, ...sponsoredQuestions]
        .sort(() => Math.random() - 0.5);
    } else {
      // Alternate pattern: interleave related and sponsored questions
      const merged = [];
      const maxLen = Math.max(relatedQuestions.length, sponsoredQuestions.length);
      for (let i = 0; i < maxLen; i++) {
        if (i < relatedQuestions.length) {
          merged.push(relatedQuestions[i]);
        }
        if (i < sponsoredQuestions.length) {
          merged.push(sponsoredQuestions[i]);
        }
      }
      return merged;
    }
  }

  /**
   * Get tooltip text for a question
   * - For sponsored questions: show the domain (base URL) only
   * - For content questions: show teaser text if available
   */
  _getTooltipText(question) {
    if (question.type === 'sponsored') {
      if (!question.url) return null;
      try {
        const url = new URL(question.url);
        return url.hostname;
      } catch {
        return question.url;
      }
    }
    // For content questions, show teaser if available
    if (question.teaser) {
      return question.teaser;
    }
    return null; // No tooltip for content questions without teaser
  }

  /**
   * Show tooltip for a question pill
   */
  _showTooltip(e, question) {
    const tooltipText = this._getTooltipText(question);
    if (!tooltipText) return;

    // Clear any pending hide timeout and previous auto-dismiss timer.
    // This also handles "another question hovered" — the old timer is cancelled
    // and a fresh 5-second window starts for the new question.
    if (this._tooltipHideTimeout) {
      clearTimeout(this._tooltipHideTimeout);
      this._tooltipHideTimeout = null;
    }
    if (this._tooltipAutoHideTimeout) {
      clearTimeout(this._tooltipAutoHideTimeout);
      this._tooltipAutoHideTimeout = null;
    }

    // Track hover start time and question
    this._tooltipHoverStartTime = Date.now();
    this._tooltipHoveredQuestion = question;

    const pill = e.currentTarget;
    const rect = pill.getBoundingClientRect();

    // Calculate position relative to viewport (for position: fixed)
    // Position tooltip below the pill since the bar is at the top of the page
    const x = rect.left + rect.width / 2;
    const y = rect.bottom; // Position right at bottom of pill (gap comes from CSS margin)

    this._tooltip = {
      visible: true,
      text: tooltipText,
      x: x,
      y: y
    };

    // Auto-dismiss after 5 seconds even if the user is still hovering
    this._tooltipAutoHideTimeout = setTimeout(() => {
      this._tooltipAutoHideTimeout = null;
      this._tooltip = null;
    }, 2000);
  }

  /**
   * Hide tooltip with a small delay to allow moving to the tooltip itself
   */
  _hideTooltip() {
    // Track the hover event with duration before hiding (only if threshold met)
    if (this._tooltipHoverStartTime && this._tooltipHoveredQuestion) {
      const hoverDurationMs = Date.now() - this._tooltipHoverStartTime;

      // Only track intentional hovers that exceed the threshold
      // This filters out quick mouse passes when users are just moving across the bar
      if (hoverDurationMs >= this._tooltipHoverThresholdMs) {
        const hoverDurationSeconds = Math.round(hoverDurationMs / 1000);

        Analytics.track(QUESTIONS_BAR_ANALYTICS_EVENTS.QUESTION_TOOLTIP_HOVERED, {
          question_id: this._tooltipHoveredQuestion.id,
          question_text: this._tooltipHoveredQuestion.text,
          question_type: this._tooltipHoveredQuestion.type,
          tooltip_text: this._getTooltipText(this._tooltipHoveredQuestion),
          question_url: this._tooltipHoveredQuestion.url || null,
          hover_duration_seconds: hoverDurationSeconds,
          hover_duration_ms: hoverDurationMs
        });
      }

      // Reset hover tracking
      this._tooltipHoverStartTime = null;
      this._tooltipHoveredQuestion = null;
    }

    // Cancel the 5-second auto-dismiss — we're hiding immediately (with the 100ms grace delay)
    if (this._tooltipAutoHideTimeout) {
      clearTimeout(this._tooltipAutoHideTimeout);
      this._tooltipAutoHideTimeout = null;
    }

    this._tooltipHideTimeout = setTimeout(() => {
      this._tooltip = null;
    }, 100);
  }

  /**
   * Keep tooltip visible when hovering over it (pointer devices only).
   * On touch-only devices iOS fires a synthetic mouseenter on the tooltip element
   * right after a tap, which would cancel the auto-dismiss timer and keep the
   * tooltip alive indefinitely. Skip the cancel on touch-only devices.
   */
  _onTooltipMouseEnter() {
    if (window.matchMedia('(hover: none)').matches) return;
    if (this._tooltipHideTimeout) {
      clearTimeout(this._tooltipHideTimeout);
      this._tooltipHideTimeout = null;
    }
  }

  /**
   * Hide tooltip when leaving the tooltip itself
   */
  _onTooltipMouseLeave() {
    if (this._tooltipAutoHideTimeout) {
      clearTimeout(this._tooltipAutoHideTimeout);
      this._tooltipAutoHideTimeout = null;
    }
    this._tooltip = null;
  }

  _renderQuestionPill(question, showAdBadge = false, index = 0) {
    const uniqueId = `${question.id || question.text}-${index}`;
    const hasTooltip = this._getTooltipText(question) !== null;

    return html`
      <span
        class="question-pill ${question.type}"
        role="button"
        tabindex="0"
        data-question-id="${question.id || ''}"
        data-question-type="${question.type}"
        data-unique-id="${uniqueId}"
        @click=${() => this._handleQuestionClick(question)}
        @keydown=${(e) => this._handleQuestionKeydown(e, question)}
        ${ref(this._observeQuestionPill(question, uniqueId))}
        ?tooltip-enabled="${hasTooltip}"
        @mouseenter=${(e) => { this._prefetchRedirectorUrl(question); if (hasTooltip) this._showTooltip(e, question); }}
        @touchstart=${(e) => { this._prefetchRedirectorUrl(question); if (hasTooltip) this._showTooltip(e, question); }}
        @mouseleave=${hasTooltip ? () => this._hideTooltip() : null}
      >
        ${question.type === 'sponsored' && showAdBadge
          ? html`<span class="ad-badge">AD</span>`
          : ''}
        ${question.text}
      </span>
    `;
  }

  _renderScrollingSection({ title, titleColor, questions, isLoading, showBorder = false, showAdBadge = false }) {
    // Duplicate questions 3x for seamless infinite scroll
    // Scrolling 50% shows all questions once, then loops seamlessly back to start
    const duplicatedQuestions = [...questions, ...questions, ...questions];

    return html`
      <div class="scrolling-section ${showBorder ? 'with-border' : ''}">
        <div class="section-label">
          <div class="section-title ${titleColor}">
            <div>${title}</div>
            <div>Questions</div>
          </div>
        </div>
        <div class="scrolling-content-wrapper">
          <div class="scrolling-content ${isLoading ? 'loading' : ''}">
            ${isLoading
              ? html`
                  <span class="loading-spinner"></span>
                  <span class="loading-text">Loading...</span>
                `
              : duplicatedQuestions.map((q, idx) => this._renderQuestionPill(q, showAdBadge, idx))
            }
          </div>
        </div>
      </div>
    `;
  }

  _renderSettingsDropdown() {
    const isMixedDisplayMode = this.displayMode === 'mixed';
    const isEnabled = !this.disabled;

    return html`
      <div class="settings-dropdown" @click=${e => e.stopPropagation()}>
        <div class="settings-header">
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          <h2>Widget Settings</h2>
        </div>

        <div class="settings-form">
          <!-- Display Mode -->
          <div class="form-section">
            <label>Questions Display Mode</label>
            <div class="radio-group">
              <label class="radio-option">
                <input
                  type="radio"
                  name="displayMode"
                  value="split"
                  ?checked=${this.displayMode === 'split'}
                  @change=${() => this._handleSettingChange('displayMode', 'split')}
                />
                <span>Split View</span>
              </label>
              <label class="radio-option">
                <input
                  type="radio"
                  name="displayMode"
                  value="mixed"
                  ?checked=${this.displayMode === 'mixed'}
                  @change=${() => this._handleSettingChange('displayMode', 'mixed')}
                />
                <span>Mixed View</span>
              </label>
            </div>
          </div>

          <!-- Mix Pattern - only if mixed mode -->
          ${isMixedDisplayMode ? html`
            <div class="form-section">
              <label>Mix Pattern</label>
              <div class="radio-group">
                <label class="radio-option">
                  <input
                    type="radio"
                    name="mixPattern"
                    value="random"
                    ?checked=${this.mixPattern === 'random'}
                    @change=${() => this._handleSettingChange('mixPattern', 'random')}
                  />
                  <span>Random</span>
                </label>
                <label class="radio-option">
                  <input
                    type="radio"
                    name="mixPattern"
                    value="alternate"
                    ?checked=${this.mixPattern === 'alternate'}
                    @change=${() => this._handleSettingChange('mixPattern', 'alternate')}
                  />
                  <span>Alternate</span>
                </label>
              </div>
            </div>
          ` : ''}

          <!--
          <!-- Answer Provider --
          <div class="form-section">
            <label>Answer Provider</label>
            <div class="radio-group">
              <label class="radio-option">
                <input
                  type="radio"
                  name="answerProvider"
                  value="mypages"
                  ?checked=${this.answerProvider === 'mypages'}
                  @change=${() => this._handleSettingChange('answerProvider', 'mypages')}
                />
                <span>My Site</span>
              </label>
              <label class="radio-option">
                <input
                  type="radio"
                  name="answerProvider"
                  value="gist.ai"
                  ?checked=${this.answerProvider === 'gist.ai'}
                  @change=${() => this._handleSettingChange('answerProvider', 'gist.ai')}
                />
                <span>Gist.ai</span>
              </label>
              <label class="radio-option">
                <input
                  type="radio"
                  name="answerProvider"
                  value="perplexity"
                  ?checked=${this.answerProvider === 'perplexity'}
                  @change=${() => this._handleSettingChange('answerProvider', 'perplexity')}
                />
                <span>Perplexity</span>
              </label>
              <label class="radio-option">
                <input
                  type="radio"
                  name="answerProvider"
                  value="brave"
                  ?checked=${this.answerProvider === 'brave'}
                  @change=${() => this._handleSettingChange('answerProvider', 'brave')}
                />
                <span>Brave</span>
              </label>
            </div>
          </div>

          <!-- Refresh Button --
          <button class="refresh-button" @click=${this._handleRefresh}>
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh Questions
          </button>
          -->
        </div>
      </div>
    `;
  }


  _renderSplitMode(showContentLoading = false, showSponsoredLoading = false) {
    return html`
      <div class="bar-content">
        <!-- Related Questions Section -->
        ${this._renderScrollingSection({
          title: 'Related',
          titleColor: 'blue',
          questions: this._contentQuestions.slice(0, 3),
          isLoading: showContentLoading,
          showBorder: true,
          showAdBadge: false
        })}

        <!-- Sponsored Questions Section -->
        ${this._renderScrollingSection({
          title: 'Sponsored',
          titleColor: 'green',
          questions: this._sponsoredQuestions.slice(0, 3),
          isLoading: showSponsoredLoading,
          showBorder: false,
          showAdBadge: false
        })}

        <!-- Settings Button (commented out) -->
        <!--
        <div class="settings-button">
          <button class="settings-button-icon" @click=${this._toggleSettings} aria-label="Settings">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </button>
          ${this._settingsOpen ? this._renderSettingsDropdown() : ''}
        </div>
        -->
      </div>
    `;
  }

  _renderMixedMode(showContentLoading = false) {
    const mixedQuestions = this._getMixedQuestions();

    return html`
      <div class="bar-content">
        <!-- Mixed Questions Section -->
        ${this._renderScrollingSection({
          title: 'Related',
          titleColor: 'blue',
          questions: mixedQuestions,
          isLoading: showContentLoading,
          showBorder: false,
          showAdBadge: true
        })}

        <!-- Settings Button (commented out) -->
        <!--
        <div class="settings-button">
          <button class="settings-button-icon" @click=${this._toggleSettings} aria-label="Settings">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </button>
          ${this._settingsOpen ? this._renderSettingsDropdown() : ''}
        </div>
        -->
      </div>
    `;
  }

  render() {
    if (!this._isValidated || this.disabled) {
      return html``;
    }

    const hasContent = Array.isArray(this._contentQuestions) && this._contentQuestions.length > 0;
    const hasSponsored = Array.isArray(this._sponsoredQuestions) && this._sponsoredQuestions.length > 0;
    const isLoading = this._isLoadingContent || this._isLoadingSponsored;

    // Hide when both APIs returned nothing (null/empty) and we are not loading
    if (!hasContent && !hasSponsored && !isLoading) {
      return html``;
    }

    // Only show spinner on the very first load (no questions cached yet).
    // Background scroll-triggered refreshes silently swap questions in place
    // without ever pausing or resetting the scroll animation.
    const showContentLoading  = this._isLoadingContent  && !hasContent;
    const showSponsoredLoading = this._isLoadingSponsored && !hasSponsored;

    const isMixed = this.displayMode === 'mixed';

    // Only apply inline styles for values that differ from defaults
    // This allows themes to provide custom values without being overridden
    const barStyles = styleMap({
      ...(this.barBackground !== '#ffffff' && { '--gist-questions-bar-background': this.barBackground }),
      ...(this.pillBackgroundSplit !== '#f8f8f8' && { '--gist-questions-pill-background-split': this.pillBackgroundSplit }),
      ...(this.pillBackgroundMixed !== '#ffffff' && { '--gist-questions-pill-background-mixed': this.pillBackgroundMixed }),
      ...(this.pillHoverBackground !== '#C8DCF9' && { '--gist-questions-pill-hover-background': this.pillHoverBackground }),
      ...(this.pillBorderSplit !== 'transparent' && { '--gist-questions-pill-border-split': this.pillBorderSplit }),
      ...(this.pillBorderMixed !== 'transparent' && { '--gist-questions-pill-border-mixed': this.pillBorderMixed }),
      ...(this.pillTextColor !== '#1456B8' && { '--gist-questions-pill-text-color': this.pillTextColor }),
      ...(this.adBadgeColor !== '#1456B8' && { '--gist-questions-ad-badge-color': this.adBadgeColor }),
      ...(this.adBadgeBackground !== 'transparent' && { '--gist-questions-ad-badge-background': this.adBadgeBackground }),
    });

    return html`
      <div class="gist-questions-bar gist-questions-bar-${this.displayMode} ${this._settingsOpen ? 'settings-open' : ''}" style="${barStyles}">
        ${isMixed ? this._renderMixedMode(showContentLoading) : this._renderSplitMode(showContentLoading, showSponsoredLoading)}
      </div>
      ${this._renderTooltip()}
    `;
  }

  _renderTooltip() {
    if (!this._tooltip || !this._tooltip.visible) {
      return html``;
    }

    return html`
      <div
        class="question-tooltip"
        style="left: ${this._tooltip.x}px; top: ${this._tooltip.y}px;"
        @mouseenter=${() => this._onTooltipMouseEnter()}
        @mouseleave=${() => this._onTooltipMouseLeave()}
      >
        <div class="question-tooltip-content">${this._tooltip.text}</div>
        <div class="question-tooltip-arrow"></div>
      </div>
    `;
  }
}

// Export initialization function for global use
export function initializeQuestionsBarWidget(container) {
  if (!container || !container.parentNode) {
    return null;
  }

  // Extract attributes from container
  const apiKey = container.getAttribute('data-api-key');
  const userId = container.getAttribute('data-user-id');
  // Force display mode to 'mixed' regardless of what partners provide via
  // the data-display-mode attribute.
  const displayMode = 'mixed';
  const mixPattern = container.getAttribute('data-mix-pattern') || 'alternate';
  const answerProvider = container.getAttribute('data-answer-provider') || 'mypages';
  const disabled = container.getAttribute('data-disabled') === 'true';
  const pageUrl = container.getAttribute('data-page-url') || '';
  const pinned = container.getAttribute('data-pinned') === 'true';

  // Extract styling attributes (defaults are set in the widget properties)
  const barBackground = container.getAttribute('data-bar-background');
  const topOffset = container.getAttribute('data-top-offset');
  const zIndexValue = container.getAttribute('data-z-index');
  const pillBackgroundSplit = container.getAttribute('data-pill-background-split');
  const pillBackgroundMixed = container.getAttribute('data-pill-background-mixed');
  const pillHoverBackground = container.getAttribute('data-pill-hover-background');
  const pillBorderSplit = container.getAttribute('data-pill-border-split');
  const pillBorderMixed = container.getAttribute('data-pill-border-mixed');
  const pillTextColor = container.getAttribute('data-pill-text-color');
  const adBadgeColor = container.getAttribute('data-ad-badge-color');
  const adBadgeBackground = container.getAttribute('data-ad-badge-background');

  // Validate required attributes
  if (!apiKey || !userId) {
    return null;
  }

  // Create and configure widget
  const widget = document.createElement('gist-questions-bar-widget');
  widget.apiKey = apiKey;
  widget.userId = userId;
  widget.displayMode = displayMode;
  widget.mixPattern = mixPattern;
  widget.answerProvider = answerProvider;
  widget.disabled = disabled;
  widget.pageUrl = pageUrl;
  widget.pinned = pinned;

  // Apply styling properties only if explicitly set (widget has its own defaults)
  if (barBackground) widget.barBackground = barBackground;
  if (topOffset) widget.topOffset = topOffset;
  if (zIndexValue) widget.zIndexValue = zIndexValue;
  if (pillBackgroundSplit) widget.pillBackgroundSplit = pillBackgroundSplit;
  if (pillBackgroundMixed) widget.pillBackgroundMixed = pillBackgroundMixed;
  if (pillHoverBackground) widget.pillHoverBackground = pillHoverBackground;
  if (pillBorderSplit) widget.pillBorderSplit = pillBorderSplit;
  if (pillBorderMixed) widget.pillBorderMixed = pillBorderMixed;
  if (pillTextColor) widget.pillTextColor = pillTextColor;
  if (adBadgeColor) widget.adBadgeColor = adBadgeColor;
  if (adBadgeBackground) widget.adBadgeBackground = adBadgeBackground;

  // Replace container with widget
  try {
    container.parentNode.replaceChild(widget, container);
    return widget;
  } catch (error) {
    logger.error('Failed to initialize widget:', error);
    return null;
  }
}

// Apply partner theme immediately (before any widget initialization)
// This ensures the theme is available regardless of how widgets are created
function applyPartnerThemeIfNeeded() {
  const themeLink = applyPartnerTheme();
  if (themeLink) {
    logger.log('Applied partner theme:', themeLink.getAttribute('data-gist-theme'));
  }
}

// Auto-initialization
function autoInitializeQuestionsBarWidgets() {
  // Apply partner theme universally based on domain
  // This runs before widget checks so styling is always available
  applyPartnerThemeIfNeeded();

  // Block bot traffic
  if (BotDetector.isBot()) {
    logger.warn('Bot detected, widget initialization blocked');
    return;
  }

  // Block sensitive pages (login, payment, etc.)
  if (shouldSkipWidgetInitialization()) {
    logger.warn('Sensitive page detected, widget initialization blocked');
    return;
  }

  const containers = document.querySelectorAll('[data-gist-widget="questions-bar"]');
  containers.forEach(container => {
    try {
      initializeQuestionsBarWidget(container);
    } catch (error) {
      logger.error('Failed to initialize questions bar widget:', error);
    }
  });
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', autoInitializeQuestionsBarWidgets);
} else {
  autoInitializeQuestionsBarWidgets();
}

// Expose to global scope
window.initializeQuestionsBarWidget = initializeQuestionsBarWidget;

